/**
 * Signing out, as every host on this device hears it.
 *
 * **Why a cookie, and why the client writes it.** Each host — a rave, `app.`,
 * `account.`, `radm.` — is its own origin with its own `localStorage`, and
 * each remembers the account last signed in there so an offline launch can
 * open the rave (`AuthProvider`'s `session-user`). Signing out on one of them
 * used to forget the person on that one only: open another rave offline and
 * they were still "signed in", on a phone they may have handed on.
 *
 * A cookie on the PLATFORM domain is the one piece of storage every host
 * shares, and it is local — so it works with no signal, which is exactly when
 * it matters. The page writes it itself (`document.cookie`, `Domain=` the
 * platform domain): a server-set marker would never be written by a sign-out
 * made offline, and comparing a server's clock with this device's would be a
 * second source of error. Both instants here are this device's.
 *
 * Two cookies, because they answer two questions:
 *
 *  - `myrave_signed_out=<epoch ms>` — WHEN the last sign-out on this device
 *    happened. A host whose remembered account is older than that forgets it.
 *    Never cleared: a later sign-in simply remembers someone later.
 *  - `myrave_signout_pending=1` — a sign-out whose revoke has NOT reached the
 *    server. The refresh token is an httpOnly cookie on the API host, and only
 *    the API can revoke it; a sign-out made offline left it alive, and the next
 *    launch with a signal quietly signed the person back in. While this is set
 *    no host refreshes: the next exchange sends the revoke first
 *    (`lib/api` `exchange`). Cleared when a revoke lands — and by the API
 *    itself on every NEW session it issues (`backend/.../auth/cookies.ts`),
 *    so a fresh sign-in, by any method, is never revoked by a stale flag.
 *
 * Beside them one marker about the way back IN: `myrave_signed_in` — who this
 * browser is signed in as, for every tab and host.
 *
 * None of them is a credential: they say "somebody signed out", "tell the
 * server", "this browser knows whom". Hence not HttpOnly — the page has to read
 * them. **A new marker here is a new cookie the privacy policy must name**
 * (`worker/privacy.test.ts`, in all 22 languages), so prefer per-tab storage
 * for anything that need not cross hosts — the sign-in-loop brake does not, and
 * lives in `app/RequireSession.tsx`.
 */
import { platformDomain } from './tenant';
import { readJson, remove, writeJson } from './storage';
import { readNativeApp } from '@myrave/shared';

export const SIGNED_OUT_COOKIE = 'myrave_signed_out';
/** Same name as `SIGNOUT_PENDING_COOKIE` in `backend/src/modules/auth/cookies.ts`. */
export const SIGNOUT_PENDING_COOKIE = 'myrave_signout_pending';

/** 400 days — the longest a browser keeps any cookie. */
const MAX_AGE = 400 * 24 * 60 * 60;

/**
 * On the platform domain, so every host reads the same one — and ONLY there:
 * a host-only copy beside it would outlive the API clearing the domain one,
 * and a stale "pending" would revoke the next session. `Domain=localhost` is
 * refused by browsers, so local development gets a host-only cookie (each
 * `*.localhost` host is its own site there anyway, see root `CLAUDE.md`).
 */
export function writePlatformCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  const domain = platformDomain().toLowerCase();
  const host = typeof location !== 'undefined' ? location.hostname.toLowerCase() : '';
  // Only a host UNDER the platform domain may name it; anywhere else (a wrong
  // origin, a test page) the browser would refuse the cookie outright.
  const under = host === domain || host.endsWith(`.${domain}`);
  const scope = domain.includes('.') && under ? `; Domain=${domain}` : '';
  try {
    document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}${scope}`;
  } catch {
    // A sandboxed document with no cookies: nothing to share, nothing to do.
  }
}

/**
 * **Delete a marker in EVERY scope it could have been written in.**
 *
 * `writePlatformCookie` names the platform domain only on a host under it, and
 * falls back to a HOST-ONLY cookie otherwise (`localhost`, or a page served on
 * a host the build's `VITE_PLATFORM_DOMAIN` does not cover). Clearing sets
 * `Domain=` the same way — so a host-only copy written once, by an older build
 * or a different address, can never be cleared again. The file's own note
 * above says what that costs: "a stale `pending` would revoke the next
 * session", which is a sign-in loop no amount of signing in can escape (the
 * user's dev machine, 2026-10-28: sign in, next host boots, logged straight
 * out, a new sign-in each time — and the authenticator code already spent).
 *
 * The API cannot help: it only ever clears `Domain=<platform>`
 * (`backend/.../auth/cookies.ts`), so the page is the only thing that can
 * reach a host-only copy. Clearing both costs one more header and removes the
 * whole class.
 */
export function clearPlatformCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const domain = platformDomain().toLowerCase();
  const host = typeof location !== 'undefined' ? location.hostname.toLowerCase() : '';
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  const under = host === domain || host.endsWith(`.${domain}`);
  // Host-only first, then the domain-scoped one — a browser keeps both.
  const scopes = ['', ...(domain.includes('.') && under ? [`; Domain=${domain}`] : [])];
  for (const scope of scopes) {
    try {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}${scope}`;
    } catch {
      // A sandboxed document with no cookies: nothing to clear.
    }
  }
}

export function readPlatformCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  let found: string | null = null;
  try {
    for (const part of document.cookie.split(';')) {
      const [k, ...rest] = part.trim().split('=');
      if (k === name) found = rest.join('=');
    }
  } catch {
    return null;
  }
  return found;
}

const SIGNED_IN_COOKIE = 'myrave_signed_in';

/**
 * Who this browser is signed in as, for every tab and host (the user,
 * 2026-10-13: "the app is open in two tabs and they are separate sessions —
 * they need to share it"): the account id and when, written on every session
 * the API gives — and `.m` after it for an account with a second step
 * (`lastSignInHasSecondStep`). A tab coming back into view compares it with its own and
 * picks up a sign-in made elsewhere (`AuthProvider`). An id, never a token.
 */
export function markSignedIn(userId: string, at = Date.now(), secondStep = false): void {
  const value = `${encodeURIComponent(userId)}.${at}${secondStep ? '.m' : ''}`;
  writePlatformCookie(SIGNED_IN_COOKIE, value, MAX_AGE);
  if (cookiesMayVanish()) writeJson(null, SIGNED_IN_COPY, value);
}

/**
 * **The Android app's copy of the sign-in marker** (the QA walk, a-offline-to-rave: "it says can't reach MyRave and
 * does not load the offline app"). Android's web view keeps cookies in MEMORY and writes them to disk only when a page
 * finishes loading — `myrave_signed_in` is written by the page after that, so an app closed before the next flush
 * started with no sign-in on record, and signed out is online only: no stored rave, "Can't reach MyRave"
 * (`offline/tenantCache.ts`). The shell now flushes as the app leaves (`mobile/CLAUDE.md`, a phone build); this copy in
 * the address's own storage, which Android writes at once, covers every build already installed. Read only when the
 * cookie is missing, only in the Android app; a sign-out made HERE clears it, one made on another host still wins by
 * `myrave_signed_out` when that cookie survived.
 */
const SIGNED_IN_COPY = 'signed-in-copy';
function cookiesMayVanish(): boolean {
  // Never throws: a sign-in must not fail on reading the user agent (a stand-in navigator throws on it).
  try { return typeof navigator !== 'undefined' && readNativeApp(navigator.userAgent)?.os === 'android'; } catch { return false; }
}

/**
 * Somebody is signed in on this device right now, as far as the device knows with no signal:
 * a sign-in on record, and no sign-out after it. Offline is for an account (the user,
 * 2026-10-27: "if the user is not signed in it's only online mode") — `offline/bundle.ts`.
 */
export function signedInHere(): string | null {
  const s = lastSignIn();
  if (!s) return null;
  const out = lastSignOutAt();
  return out !== null && out >= s.at ? null : s.userId;
}

/** The last sign-in on this browser — or null. */
/**
 * The signed-in account CHANGED on this device (a new photo — the user, 2026-10-30: "the profile picture in the
 * header is not updating"): the sign-in marker is stamped again, same account and second step, so every other
 * MyRave tab — each host its own origin — refreshes its session when it is next in view (`AuthProvider`).
 */
export function noteAccountChanged(): void {
  const s = readSignedIn();
  if (s) markSignedIn(s.userId, Date.now(), s.secondStep);
}

export function lastSignIn(): { userId: string; at: number } | null {
  const s = readSignedIn();
  return s ? { userId: s.userId, at: s.at } : null;
}

/**
 * **The last sign-in on this browser was an account with a second step** — an admin role, whose every
 * sign-in owes a passkey or a code (step 70, `app/RequireMfa.tsx`). The `.m` on the marker, written from
 * the session's own answer (`mfa.required`); known with no network, on every host, before any session
 * has landed — which is what lets the gate hold the launch screen for an admin on a slow start (the user,
 * 2026-10-29: "still shows the page before asking the passkey or 2FA") and nobody else. Not a secret, and
 * not a permission: the API decides what an admin may do whatever this says.
 */
export function lastSignInHasSecondStep(): boolean {
  return readSignedIn()?.secondStep ?? false;
}

function readSignedIn(): { userId: string; at: number; secondStep: boolean } | null {
  const cookie = readPlatformCookie(SIGNED_IN_COOKIE);
  const v = cookie ?? (cookiesMayVanish() ? readJson<string | null>(null, SIGNED_IN_COPY, null) : null);
  const m = v ? /^(.+?)\.(\d+)(\.m)?$/.exec(v) : null;
  if (!m) return null;
  try { return { userId: decodeURIComponent(m[1]!), at: Number(m[2]), secondStep: Boolean(m[3]) }; } catch { return null; }
}

/**
 * **A sign-in just came back from a provider** — the API's two-minute outcome cookie (`myrave_signin=ok`
 * or `created`, `backend/.../oauthRoutes.ts` `sendHome`: Google, Apple, Facebook, the phone app's
 * hand-off). Read ONCE per page and remembered, because `SignInOutcome` deletes the cookie in its first
 * effect, after the first render. The page it lands on knows a session is coming before the boot refresh
 * has said whose — the gate holds the launch screen for it rather than drawing the app first.
 */
let freshAtLoad: boolean | null = null;
export function freshSignIn(): boolean {
  freshAtLoad ??= /^(ok|created)$/.test(readPlatformCookie('myrave_signin') ?? '');
  return freshAtLoad;
}
/** Tests only: read the outcome cookie again. */
export function forgetFreshSignIn(): void { freshAtLoad = null; }
// As this module loads — with the entry, before anything renders or deletes the cookie.
freshSignIn();

/**
 * Record a sign-out on this device, with its revoke still to be sent — and
 * WHICH sign-in it ends (`family`, the access token's `fam`), so the revoke
 * can never end a newer one. On the user's iPhone (2026-10-16) the owed flag
 * outlived a new sign-in (the web view's `document.cookie` lags behind a
 * response's Set-Cookie), and the revoke, carrying the new cookie, ended it.
 */
export function markSignedOut(at = Date.now(), family: string | null = null): void {
  remove(null, SIGNED_IN_COPY);
  // The raves kept for offline are the account's (`keptRaves.ts`): gone with it.
  clearPlatformCookie('myrave_kept_raves');
  writePlatformCookie(SIGNED_OUT_COOKIE, String(at), MAX_AGE);
  writePlatformCookie(SIGNOUT_PENDING_COOKIE, family ? encodeURIComponent(family) : '1', MAX_AGE);
}

/** The sign-in an owed revoke is for — null when it was not recorded ('1'). */
export function pendingFamily(): string | null {
  const v = readPlatformCookie(SIGNOUT_PENDING_COOKIE);
  if (!v || v === '1') return null;
  try { return decodeURIComponent(v); } catch { return null; }
}

/** When the last sign-out on this device happened, epoch ms — or null. */
export function lastSignOutAt(): number | null {
  const v = readPlatformCookie(SIGNED_OUT_COOKIE);
  const n = v ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

/** Is a sign-out's revoke still to reach the server? */
export function revokePending(): boolean {
  return Boolean(readPlatformCookie(SIGNOUT_PENDING_COOKIE));
}

/** The revoke reached the server, or a new session superseded it. */
export function revokeSettled(): void {
  clearPlatformCookie(SIGNOUT_PENDING_COOKIE);
}

